import {
  AHText,
  StoreFormattedDate,
  AHIconProps,
  AHIcon,
  TodoPrioIcon,
  TodoStatusCircle,
  Box,
  AHTeamIconRelay,
  Circle,
  SignedActorCompact,
  SignedActor,
} from "arkh";
import { useIntl, FormattedMessage } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { PRIORITIES } from "../TodoPriorities";
import { TodoTimelineUpdateEventIcon_event$key } from "./__generated__/TodoTimelineUpdateEventIcon_event.graphql";
import { TodoTimelineUpdateEvent_event$key } from "./__generated__/TodoTimelineUpdateEvent_event.graphql";
import { TodoTimelineUpdateEvent_timelineItem$key } from "./__generated__/TodoTimelineUpdateEvent_timelineItem.graphql";
import { TodoTimelineUpdateEventBody_event$key } from "./__generated__/TodoTimelineUpdateEventBody_event.graphql";
import { TodoTimelineUpdateEventTag_tag$key } from "./__generated__/TodoTimelineUpdateEventTag_tag.graphql";
import { TodoTimelineUpdateEventTeam_team$key } from "./__generated__/TodoTimelineUpdateEventTeam_team.graphql";
import { TodoTimelineUpdateEventEmployee_employee$key } from "./__generated__/TodoTimelineUpdateEventEmployee_employee.graphql";
import { TodoTimelineUpdateEventRole_role$key } from "./__generated__/TodoTimelineUpdateEventRole_role.graphql";
import { TodoTimelineUpdateEventCreated_todo$key } from "./__generated__/TodoTimelineUpdateEventCreated_todo.graphql";
import React, { useRef } from "react";

export function TodoTimelineCreatedEvent(props: {
  todo: TodoTimelineUpdateEventCreated_todo$key;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoTimelineUpdateEventCreated_todo on Todo {
        createdByActor {
          ...SignedActor_actor
        }
        createdAtDate {
          ...StoreFormattedDate
        }
      }
    `,
    props.todo
  );
  if (!todo.createdByActor) {
    return null;
  }
  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1,
      }}
      spaceHorizontal="small"
    >
      <Box
        style={{
          flexDirection: "row",
          alignItems: "center",

          flexShrink: 1,
        }}
        gap="xsmall"
      >
        {todo.createdByActor ? (
          <Box spaceRight="xsmall">
            <SignedActor signedActor={todo.createdByActor} />
          </Box>
        ) : null}

        <EventTextLabel>
          <FormattedMessage
            defaultMessage="created"
            description="Todo Edit Timeline Event Created"
          />
        </EventTextLabel>

        <AHText
          color="text-washed"
          style={{ flexShrink: 0 }}
          numberOfLines={1}
          type="detail"
        >
          {todo.createdAtDate ? (
            <StoreFormattedDate date={todo.createdAtDate} />
          ) : null}
        </AHText>
      </Box>
    </Box>
  );
}
export function TodoTimelineUpdateEvent(props: {
  event: TodoTimelineUpdateEvent_event$key;
  timelineItem: TodoTimelineUpdateEvent_timelineItem$key;
}) {
  const timelineItem = useFragment(
    graphql`
      fragment TodoTimelineUpdateEvent_timelineItem on TodoTimelineUpdate {
        createdAtDate {
          ...StoreFormattedDate
        }
        createdByActor {
          ...SignedActorCompact_actor
          ...SignedActor_actor
        }
      }
    `,
    props.timelineItem
  );
  const event = useFragment(
    graphql`
      fragment TodoTimelineUpdateEvent_event on TodoTimelineUpdateEvent {
        __typename
        ...TodoTimelineUpdateEventIcon_event
        ...TodoTimelineUpdateEventBody_event
        ... on TodoTimelineUpdateEventStatus {
          newStatus
        }
      }
    `,
    props.event
  );
  const isStatusComplete =
    event.__typename === "TodoTimelineUpdateEventStatus" &&
    event.newStatus === "DONE";

  const shouldNotWrap = event.__typename === "TodoTimelineUpdateEventTitle";
  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1,
      }}
      spaceHorizontal="small"
    >
      {isStatusComplete ? null : (
        <Circle size="smallMedium" color="surface-background-light">
          <EventIcon event={event} />
        </Circle>
      )}
      <Box
        style={{
          flexDirection: "row",
          alignItems: "center",
          flexWrap: shouldNotWrap ? "nowrap" : "wrap",
          flexShrink: 1,
        }}
        spaceLeft={isStatusComplete ? "none" : "small"}
        gap="xsmall"
      >
        {timelineItem.createdByActor ? (
          <Box spaceRight="xsmall">
            {isStatusComplete ? (
              <SignedActor signedActor={timelineItem.createdByActor} />
            ) : (
              <SignedActorCompact signedActor={timelineItem.createdByActor} />
            )}
          </Box>
        ) : null}

        <EventBody event={event} />

        <AHText
          color="text-washed"
          style={{ flexShrink: 0 }}
          numberOfLines={1}
          type="detail"
        >
          {timelineItem.createdAtDate ? (
            <StoreFormattedDate date={timelineItem.createdAtDate} />
          ) : null}
        </AHText>
      </Box>
    </Box>
  );
}

function EventIcon(props: { event: TodoTimelineUpdateEventIcon_event$key }) {
  const event = useFragment(
    graphql`
      fragment TodoTimelineUpdateEventIcon_event on TodoTimelineUpdateEvent {
        __typename
        ... on TodoTimelineUpdateEventAssignedEmployees {
          __typename
        }
        ... on TodoTimelineUpdateEventTitle {
          __typename
        }
        ... on TodoTimelineUpdateEventBody {
          __typename
        }
        ... on TodoTimelineUpdateEventStatus {
          newStatus
          __typename
        }
        ... on TodoTimelineUpdateEventPriority {
          newPriority
          __typename
        }
        ... on TodoTimelineUpdateEventCategory {
          __typename
        }
        ... on TodoTimelineUpdateEventExpireAt {
          __typename
        }
        ... on TodoTimelineUpdateEventTags {
          __typename
        }
        ... on TodoTimelineUpdateEventAssignedTeams {
          __typename
        }
        ... on TodoTimelineUpdateEventAssignedRoles {
          __typename
        }
      }
    `,
    props.event
  );

  const iconProps: Partial<AHIconProps> = {
    size: "xsmall",
    color: "text-secondary",
  };
  switch (event.__typename) {
    case "TodoTimelineUpdateEventAssignedEmployees":
      return <AHIcon type="user" {...iconProps} />;
    case "TodoTimelineUpdateEventPriority":
      return <TodoPrioIcon size="xsmall" priority={event.newPriority} />;
    case "TodoTimelineUpdateEventExpireAt":
      return <AHIcon type="deadline" {...iconProps} />;
    case "TodoTimelineUpdateEventTags":
      return <AHIcon type="tag" {...iconProps} />;
    case "TodoTimelineUpdateEventAssignedTeams":
      return <AHIcon type="team" {...iconProps} />;
    case "TodoTimelineUpdateEventAssignedRoles":
      return <AHIcon type="employee-role" {...iconProps} />;
    case "TodoTimelineUpdateEventCategory":
    case "TodoTimelineUpdateEventTitle":
    case "TodoTimelineUpdateEventBody":
      return <AHIcon type="edit" {...iconProps} />;
    case "TodoTimelineUpdateEventStatus":
      return (
        <TodoStatusCircle
          status={event.newStatus}
          size="xsmall"
          expireAtISO={null}
        />
      );
  }

  return null;
}
function EventTextLabel(props: { children: React.ReactNode }) {
  return (
    <AHText
      type="detail"
      color="text-secondary"
      numberOfLines={1}
      style={{ flexShrink: 1 }}
    >
      {props.children}
    </AHText>
  );
}

function EventBody(props: { event: TodoTimelineUpdateEventBody_event$key }) {
  const event = useFragment(
    graphql`
      fragment TodoTimelineUpdateEventBody_event on TodoTimelineUpdateEvent {
        __typename
        ... on TodoTimelineUpdateEventAssignedEmployees {
          addedAssignedEmployees {
            id
            ...TodoTimelineUpdateEventEmployee_employee
          }
          removedAssignedEmployees {
            id
            ...TodoTimelineUpdateEventEmployee_employee
          }
          __typename
        }

        ... on TodoTimelineUpdateEventTitle {
          newTitle
          __typename
        }
        ... on TodoTimelineUpdateEventBody {
          newBody
          __typename
        }
        ... on TodoTimelineUpdateEventStatus {
          newStatus
          __typename
        }
        ... on TodoTimelineUpdateEventPriority {
          newPriority
          __typename
        }
        ... on TodoTimelineUpdateEventCategory {
          newCategory {
            title
          }
          __typename
        }
        ... on TodoTimelineUpdateEventExpireAt {
          __typename
          newExpireAt {
            ...StoreFormattedDate
          }
        }
        ... on TodoTimelineUpdateEventTags {
          addedTodoTags {
            id
            ...TodoTimelineUpdateEventTag_tag
          }
          removedTodoTags {
            id
            ...TodoTimelineUpdateEventTag_tag
          }
          __typename
        }
        ... on TodoTimelineUpdateEventAssignedTeams {
          addedAssignedTeams {
            id
            ...TodoTimelineUpdateEventTeam_team
          }
          removedAssignedTeams {
            id
            ...TodoTimelineUpdateEventTeam_team
          }
          __typename
        }
        ... on TodoTimelineUpdateEventAssignedRoles {
          addedAssignedRoles {
            id
            ...TodoTimelineUpdateEventRole_role
          }
          removedAssignedRoles {
            id
            ...TodoTimelineUpdateEventRole_role
          }
          __typename
        }
      }
    `,
    props.event
  );

  const intl = useIntl();

  switch (event.__typename) {
    case "TodoTimelineUpdateEventTitle":
      return (
        <>
          <EventTextLabel>
            <FormattedMessage
              defaultMessage={`set title: "{newTitle}"`}
              description="Todo Edit Timeline Event Title Changed"
              values={{ newTitle: event.newTitle ?? "" }}
            />
          </EventTextLabel>
        </>
      );
    case "TodoTimelineUpdateEventBody":
      return (
        <EventTextLabel>
          <FormattedMessage
            defaultMessage={`set description`}
            description="Todo Edit Timeline Event Body Changed"
            values={{
              newDescription: event.newBody ?? "",
            }}
          />
        </EventTextLabel>
      );
    case "TodoTimelineUpdateEventCategory":
      return (
        <EventTextLabel>
          <FormattedMessage
            defaultMessage={`moved to list: "{newCategory}"`}
            description="Todo Edit Timeline Event Category Changed"
            values={{ newCategory: event.newCategory?.title ?? "" }}
          />
        </EventTextLabel>
      );
    case "TodoTimelineUpdateEventPriority":
      const priorityLabel = PRIORITIES.find(
        (p) => p.value === event.newPriority
      )?.label;
      return (
        <EventTextLabel>
          <FormattedMessage
            defaultMessage={`set priority to  {newPriority}`}
            description="Todo Edit Timeline Event Priority Changed"
            values={{ newPriority: priorityLabel ?? "" }}
          />
        </EventTextLabel>
      );
    case "TodoTimelineUpdateEventExpireAt":
      return (
        <EventTextLabel>
          <FormattedMessage
            defaultMessage={`changed due date to "{newDueDate}"`}
            description="Todo Edit Timeline Event Due Date Changed"
            values={{
              newDueDate: event.newExpireAt ? (
                <StoreFormattedDate date={event.newExpireAt} />
              ) : null,
            }}
          />
        </EventTextLabel>
      );
    case "TodoTimelineUpdateEventStatus":
      return (
        <EventTextLabel>
          {event.newStatus === "DONE" ? (
            <FormattedMessage
              defaultMessage={`marked as completed`}
              description="Todo Edit Timeline Event Status Changed"
              values={{ newStatus: event.newStatus ?? "" }}
            />
          ) : (
            <FormattedMessage
              defaultMessage={`marked as not completed`}
              description="Todo Edit Timeline Event Status Changed"
              values={{ newStatus: event.newStatus ?? "" }}
            />
          )}
        </EventTextLabel>
      );
    case "TodoTimelineUpdateEventAssignedEmployees": {
      const messages: Item[] = [];
      if (event.addedAssignedEmployees?.length > 0) {
        messages.push({
          label: intl.formatMessage({
            defaultMessage: "assigned",
            description: "Todo Edit Timeline Event Assigned Employees Added",
          }),
          items: event.addedAssignedEmployees?.map((e, i) => (
            <TodoTimelineUpdateEventEmployee
              employee={e}
              key={e.id + "added-employee"}
            />
          )),
        });
      }

      if (event.removedAssignedEmployees?.length > 0) {
        messages.push({
          label: intl.formatMessage(
            {
              defaultMessage: `{itemCount, plural, one {unassigned} other {unassigned}}`,
              description:
                "Todo Edit Timeline Event Assigned Employees Removed",
            },
            {
              itemCount: event.removedAssignedEmployees?.length ?? 0,
            }
          ),
          items: event.removedAssignedEmployees?.map((e, i) => (
            <TodoTimelineUpdateEventEmployee
              employee={e}
              key={e.id + "removed-employee"}
            />
          )),
        });
      }
      return <ItemsList items={messages} />;
    }

    case "TodoTimelineUpdateEventTags": {
      const messages: Item[] = [];
      if (event.addedTodoTags?.length > 0) {
        messages.push({
          label: intl.formatMessage(
            {
              defaultMessage: `{itemCount, plural, one {added tag} other {added tags}}`,
              description: "Todo Edit Timeline Event Tags Added",
            },
            {
              itemCount: event.addedTodoTags?.length ?? 0,
            }
          ),
          items: event.addedTodoTags?.map((tag, i) => (
            <TodoTimelineUpdateEventTag tag={tag} key={tag.id + "added-tag"} />
          )),
        });
      }

      if (event.removedTodoTags?.length > 0) {
        messages.push({
          label: intl.formatMessage(
            {
              defaultMessage: `{itemCount, plural, one {removed tag} other {removed tags}}`,
              description: "Todo Edit Timeline Event Tags Removed",
            },
            {
              itemCount: event.removedTodoTags?.length ?? 0,
            }
          ),
          items: event.removedTodoTags?.map((tag, i) => (
            <TodoTimelineUpdateEventTag
              tag={tag}
              key={tag.id + "removed-tag"}
            />
          )),
        });
      }
      return <ItemsList items={messages} />;
    }

    case "TodoTimelineUpdateEventAssignedTeams":
      const messages: Item[] = [];
      if (event.addedAssignedTeams?.length > 0) {
        messages.push({
          label: intl.formatMessage(
            {
              description: "Todo Edit Timeline Event Added Teams Changed",
              defaultMessage: `{itemCount, plural, one {assigned team} other {assigned teams}}`,
            },
            {
              itemCount: event.addedAssignedTeams?.length ?? 0,
            }
          ),
          items: event.addedAssignedTeams?.map(
            (team, i) => (
              <TodoTimelineUpdateEventTeam
                team={team}
                key={team.id + "added-team"}
              />
            ),
            {
              itemCount: event.addedAssignedTeams?.length ?? 0,
            }
          ),
        });
      }
      if (event.removedAssignedTeams?.length > 0) {
        messages.push({
          label: intl.formatMessage(
            {
              description: "Todo Edit Timeline Event Removed Teams Changed",
              defaultMessage: `{itemCount, plural, one {unassigned team} other {unassigned teams}}`,
            },
            {
              itemCount: event.removedAssignedTeams?.length ?? 0,
            }
          ),
          items: event.removedAssignedTeams?.map((team, i) => (
            <TodoTimelineUpdateEventTeam
              team={team}
              key={team.id + "removed-team"}
            />
          )),
        });
      }
      return <ItemsList items={messages} />;

    case "TodoTimelineUpdateEventAssignedRoles": {
      const messages: Item[] = [];
      if (event.addedAssignedRoles?.length > 0) {
        messages.push({
          label: intl.formatMessage(
            {
              description: "Todo Edit Timeline Event Added Roles Changed",
              defaultMessage: `{itemCount, plural, one {assigned role} other {assigned roles}}`,
            },
            {
              itemCount: event.addedAssignedRoles?.length ?? 0,
            }
          ),
          items: event.addedAssignedRoles?.map((role) => (
            <TodoTimelineUpdateEventRole
              role={role}
              key={role.id + "added-role"}
            />
          )),
        });
      }
      if (event.removedAssignedRoles?.length > 0) {
        messages.push({
          label: intl.formatMessage(
            {
              description: "Todo Edit Timeline Event Removed Roles Changed",
              defaultMessage: `{itemCount, plural, one {unassigned role} other {unassigned roles}}`,
            },
            {
              itemCount: event.removedAssignedRoles?.length ?? 0,
            }
          ),
          items: event.removedAssignedRoles?.map((role) => (
            <TodoTimelineUpdateEventRole
              role={role}
              key={role.id + "removed-role"}
            />
          )),
        });
      }
      return <ItemsList items={messages} />;
    }
  }

  return event.__typename;
}

function TodoTimelineUpdateEventTeam(props: {
  team: TodoTimelineUpdateEventTeam_team$key;
}) {
  const team = useFragment(
    graphql`
      fragment TodoTimelineUpdateEventTeam_team on Team {
        name
        color
        icon
        ...AHTeamIcon
      }
    `,
    props.team
  );
  return (
    <>
      <AHTeamIconRelay
        team={team}
        size="xxsmall"
        customColor={team.color ?? "surface-background-light"}
      />
      <AHText type="detail" spaceLeft="xxsmall" color="text-secondary">
        {team.name}
      </AHText>
    </>
  );
}

function TodoTimelineUpdateEventTag(props: {
  tag: TodoTimelineUpdateEventTag_tag$key;
}) {
  const tag = useFragment(
    graphql`
      fragment TodoTimelineUpdateEventTag_tag on TodoCategoryTag {
        title
        color
      }
    `,
    props.tag
  );
  return (
    <>
      <Circle size="dot" style={{ backgroundColor: tag.color }} />
      <AHText type="detail" spaceLeft="xxsmall" color="text-secondary">
        {tag.title}
      </AHText>
    </>
  );
}
function TodoTimelineUpdateEventEmployee(props: {
  employee: TodoTimelineUpdateEventEmployee_employee$key;
}) {
  const employee = useFragment(
    graphql`
      fragment TodoTimelineUpdateEventEmployee_employee on Employee {
        name
      }
    `,
    props.employee
  );
  return (
    <>
      <AHText type="detail" numberOfLines={1} color="text-secondary">
        {employee.name}
      </AHText>
    </>
  );
}

function TodoTimelineUpdateEventRole(props: {
  role: TodoTimelineUpdateEventRole_role$key;
}) {
  const role = useFragment(
    graphql`
      fragment TodoTimelineUpdateEventRole_role on EmployeeRole {
        name
      }
    `,
    props.role
  );
  return (
    <>
      <AHText type="detail" numberOfLines={1} color="text-secondary">
        {role.name}
      </AHText>
    </>
  );
}

type Item = { label: string; items: React.ReactNode[] };
function ItemsList(props: { items: Item[] }) {
  return (
    <>
      {props.items.map((item, index) => (
        <React.Fragment key={index + ""}>
          <EventTextLabel>{item.label}</EventTextLabel>
          {item.items.map((item, ii) => (
            <Box
              key={ii + ""}
              style={{
                flexDirection: "row",
                alignItems: "baseline",
              }}
            >
              {item}
            </Box>
          ))}

          {index < props.items.length - 1 ? <Spacer /> : ""}
        </React.Fragment>
      ))}
    </>
  );
}
function Spacer() {
  const intl = useIntl();
  const intlAnd = intl.formatMessage({
    defaultMessage: "and",
    description: "Todo Edit Timeline Event And",
  });
  return (
    <AHText type="detail" spaceHorizontal="xsmall" color="text-secondary">
      {intlAnd}
    </AHText>
  );
}
